import { useEffect, useState } from "react";

/**
 * Home-screen app install state, shared by the floating install card, the
 * "Get the app" bar on Home / Listings and the footer button.
 * The browser's install event can fire before React mounts, so it is captured here at load.
 */
const INSTALLED = "dmv_app_installed";
let deferred = null;
const subs = new Set();
const emit = () => subs.forEach((f) => { try { f(); } catch {} });

export const isStandalone = () =>
  typeof window !== "undefined" && (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true);
export const isIOS = () =>
  typeof navigator !== "undefined" && (/iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));
export const isMobile = () => typeof navigator !== "undefined" && /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);

const markInstalled = () => { try { localStorage.setItem(INSTALLED, String(Date.now())); } catch {} };
export function isInstalled() {
  if (isStandalone()) return true;
  try { return !!localStorage.getItem(INSTALLED); } catch { return false; }
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferred = e;
    // the browser only offers this when the app is NOT installed, so clear any stale flag
    try { localStorage.removeItem(INSTALLED); } catch {}
    emit();
  });
  window.addEventListener("appinstalled", () => { deferred = null; markInstalled(); emit(); });
  if (isStandalone()) markInstalled();
}

export const canPrompt = () => !!deferred;

/** Show the browser's own install sheet. Returns "accepted" | "dismissed" | null. */
export async function promptInstall() {
  if (!deferred) return null;
  const d = deferred;
  d.prompt();
  let res = null;
  try { res = await d.userChoice; } catch {}
  deferred = null;
  if (res?.outcome === "accepted") markInstalled();
  emit();
  return res?.outcome || null;
}

export function subscribe(fn) { subs.add(fn); return () => subs.delete(fn); }

export function useInstallState() {
  const [, force] = useState(0);
  useEffect(() => subscribe(() => force((x) => x + 1)), []);
  return { canPrompt: !!deferred, installed: isInstalled(), ios: isIOS(), mobile: isMobile() };
}

/** Ask the floating card to open (it shows the install button or the iOS / browser-menu steps). */
export function openInstall() { window.dispatchEvent(new Event("dmv:install")); }
